// src/pages/Stock/StockDetail.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, collection, query, onSnapshot, orderBy, writeBatch, serverTimestamp, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import ImageUpload from '../../components/ImageUpload';
import * as XLSX from 'xlsx';
import BarcodePrintModal from '../../components/BarcodePrintModal';
import toast from 'react-hot-toast';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        color: '#64748b',
        fontSize: '14px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '6px',
        transition: 'all 0.2s',
        background: '#f1f5f9'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '30px',
        alignItems: 'start'
    },
    box: {
        background: 'white',
        padding: '28px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    // Summary Cards - Compact
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        marginBottom: '24px'
    },
    summaryCard: {
        background: 'white',
        padding: '14px 16px',
        borderRadius: '12px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        textAlign: 'center',
        transition: 'transform 0.2s, box-shadow 0.2s'
    },
    summaryCardHover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    summaryIcon: {
        fontSize: '20px',
        marginBottom: '6px'
    },
    summaryValue: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        marginBottom: '2px'
    },
    summaryLabel: {
        fontSize: '11px',
        color: '#64748b',
        fontWeight: '500'
    },
    // Status badges
    statusBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600'
    },
    statusCritical: {
        backgroundColor: '#fee2e2',
        color: '#dc2626'
    },
    statusWarning: {
        backgroundColor: '#fef3c7',
        color: '#d97706'
    },
    statusNormal: {
        backgroundColor: '#dcfce7',
        color: '#06C167'
    },
    // Info list
    infoList: {
        listStyle: 'none',
        padding: 0,
        margin: 0
    },
    infoItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '14px 0',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'center'
    },
    infoLabel: {
        color: '#64748b',
        fontSize: '14px',
        fontWeight: '500'
    },
    infoValue: {
        fontWeight: '600',
        color: '#0f172a',
        fontSize: '14px',
        textAlign: 'right'
    },
    // Form elements
    formGroup: {
        marginBottom: '16px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 14px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 14px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        background: 'white',
        outline: 'none'
    },
    // Buttons
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    buttonSecondary: {
        padding: '10px 20px',
        border: '1px solid #e2e8f0',
        background: 'white',
        color: '#374151',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    buttonSuccess: {
        background: 'linear-gradient(135deg, #10b981, #059669)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
    },
    buttonDanger: {
        background: '#ef4444',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
    },
    // Product image
    productImage: {
        width: '100%',
        height: 'auto',
        maxHeight: '220px',
        borderRadius: '12px',
        marginBottom: '16px',
        border: '1px solid #e2e8f0',
        objectFit: 'cover'
    },
    productImagePlaceholder: {
        width: '100%',
        height: '180px',
        borderRadius: '12px',
        marginBottom: '16px',
        background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#94a3b8',
        fontSize: '48px'
    },
    // Barcode display
    barcodeContainer: {
        background: '#f8fafc',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    barcodeText: {
        fontFamily: '"Courier New", monospace',
        fontSize: '18px',
        fontWeight: '600',
        color: '#0f172a',
        letterSpacing: '2px'
    },
    copyButton: {
        padding: '8px 12px',
        border: 'none',
        background: '#e2e8f0',
        color: '#475569',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        transition: 'all 0.2s'
    },
    // Table
    tableContainer: {
        maxHeight: '400px',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        color: '#334155',
        fontSize: '14px'
    },
    // Filters
    filterContainer: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
    },
    filterInput: {
        padding: '10px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        minWidth: '150px'
    },
    filterSelect: {
        padding: '10px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        background: 'white',
        outline: 'none',
        minWidth: '130px'
    },
    // Movement type badges
    movementIn: {
        color: '#06C167',
        fontWeight: '600'
    },
    movementOut: {
        color: '#dc2626',
        fontWeight: '600'
    }
};

function StockDetail({ productId: propProductId, onBack: propOnBack }) {
    const { productId: paramsProductId } = useParams();
    const productId = propProductId || paramsProductId;
    const navigate = useNavigate();
    const { userData, canViewPrices } = useAuth();
    const [product, setProduct] = useState(null);
    const [editedProduct, setEditedProduct] = useState(null);
    const [productGroups, setProductGroups] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [manualEntry, setManualEntry] = useState({
        quantity: '',
        type: 'Giriş',
        description: '',
        warehouseId: ''
    });

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [copiedBarcode, setCopiedBarcode] = useState(false);
    const [relatedVariants, setRelatedVariants] = useState([]);

    useEffect(() => {
        if (!userData?.companyId || !productId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const productRef = doc(db, 'companies', companyId, 'products', productId);
        const unsubProduct = onSnapshot(productRef, (docSnap) => {
            if (docSnap.exists()) {
                const productData = { id: docSnap.id, ...docSnap.data() };
                setProduct(productData);
                setEditedProduct(productData);
            }
        });

        const groupsQuery = query(collection(db, 'companies', companyId, 'productGroups'));
        const unsubGroups = onSnapshot(groupsQuery, (snapshot) => {
            setProductGroups(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const warehousesQuery = query(collection(db, 'companies', companyId, 'warehouses'));
        const unsubWarehouses = onSnapshot(warehousesQuery, (snapshot) => {
            setWarehouses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const movementsQuery = query(
            collection(db, 'companies', companyId, 'products', productId, 'movements'),
            orderBy('date', 'desc')
        );
        const unsubMovements = onSnapshot(movementsQuery, (snapshot) => {
            setMovements(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => {
            unsubProduct();
            unsubMovements();
            unsubGroups();
            unsubWarehouses();
        };
    }, [userData, productId]);

    useEffect(() => {
        if (!userData?.companyId || !product?.variantGroupId) {
            setRelatedVariants([]);
            return;
        }

        const q = query(
            collection(db, 'companies', userData.companyId, 'products'),
            where('variantGroupId', '==', product.variantGroupId)
        );

        const unsubRelated = onSnapshot(q, (snapshot) => {
            const others = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(p => p.id !== productId);
            setRelatedVariants(others);
        });

        return () => unsubRelated();
    }, [userData, product?.variantGroupId, productId]);

    // Filtered movements
    const filteredMovements = useMemo(() => {
        return movements.filter(move => {
            // Search filter
            if (searchTerm && !move.type?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Type filter
            if (filterType !== 'all') {
                const isIn = move.quantity?.includes('+') || move.type?.includes('Giriş');
                const isOut = move.quantity?.includes('-') || move.type?.includes('Çıkış');
                if (filterType === 'in' && !isIn) return false;
                if (filterType === 'out' && !isOut) return false;
            }

            // Date filters
            if (move.date && filterDateFrom) {
                const moveDate = new Date(move.date.seconds * 1000);
                const fromDate = new Date(filterDateFrom);
                if (moveDate < fromDate) return false;
            }

            if (move.date && filterDateTo) {
                const moveDate = new Date(move.date.seconds * 1000);
                const toDate = new Date(filterDateTo);
                toDate.setHours(23, 59, 59);
                if (moveDate > toDate) return false;
            }

            return true;
        });
    }, [movements, searchTerm, filterType, filterDateFrom, filterDateTo]);

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setEditedProduct(prev => ({ ...prev, [name]: checked }));
        } else {
            setEditedProduct(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        const productRef = doc(db, 'companies', userData.companyId, 'products', productId);
        try {
            const { name, barcode, groupId, unit, criticalLevel, salePrice, isRawMaterial } = editedProduct;
            await updateDoc(productRef, {
                name,
                barcode,
                groupId,
                unit,
                criticalLevel: Number(criticalLevel),
                salePrice: Number(salePrice),
                isRawMaterial: Boolean(isRawMaterial)
            });
            setIsEditing(false);
        } catch (error) {
            console.error("Ürün güncellenirken hata:", error);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) {
            return;
        }

        try {
            const productRef = doc(db, 'companies', userData.companyId, 'products', productId);

            // Delete product
            await deleteDoc(productRef);

            // Note: Subcollections (movements) are not automatically deleted in Firestore.
            // For a complete cleanup, you'd need a cloud function or client-side loop.
            // But leaving them is usually fine as they become inaccessible via UI.

            toast.success('Ürün başarıyla silindi.');
            navigate('/stok');
        } catch (error) {
            console.error("Ürün silinirken hata:", error);
            toast.error("Ürün silinirken bir hata oluştu.");
        }
    };

    const handleImageUpload = (imageData) => {
        if (imageData) {
            const productRef = doc(db, 'companies', userData.companyId, 'products', productId);
            updateDoc(productRef, {
                imageUrl: imageData.thumbnailUrl,
                imageData: imageData
            }).then(() => {
                console.log('Ürün görseli güncellendi');
            }).catch((error) => {
                console.error("Fotoğraf güncellenirken hata:", error);
                toast.error("Fotoğraf güncellenemedi.");
            });
        } else {
            const productRef = doc(db, 'companies', userData.companyId, 'products', productId);
            updateDoc(productRef, {
                imageUrl: '',
                imageData: null
            }).then(() => {
                console.log('Ürün görseli kaldırıldı');
            }).catch((error) => {
                console.error("Fotoğraf kaldırılırken hata:", error);
                toast.error("Fotoğraf kaldırılamadı.");
            });
        }
    };

    const handleManualChange = (e) => {
        const { name, value } = e.target;
        setManualEntry(prev => ({ ...prev, [name]: value }));
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        const amount = Number(manualEntry.quantity);
        if (!amount || amount <= 0 || !manualEntry.description) {
            toast.error("Lütfen miktar ve açıklama alanlarını doldurun.");
            return;
        }

        const companyId = userData.companyId;
        const productRef = doc(db, 'companies', companyId, 'products', productId);
        const movementRef = doc(collection(productRef, 'movements'));
        const batch = writeBatch(db);

        let newTotalQuantity = product.quantity;
        let newWarehouseQuantities = { ...product.warehouseQuantities } || {};

        if (manualEntry.warehouseId) {
            const currentWarehouseQuantity = newWarehouseQuantities[manualEntry.warehouseId] || 0;
            const newWarehouseQuantity = manualEntry.type === 'Giriş'
                ? currentWarehouseQuantity + amount
                : currentWarehouseQuantity - amount;

            newWarehouseQuantities[manualEntry.warehouseId] = newWarehouseQuantity;

            newTotalQuantity = manualEntry.type === 'Giriş'
                ? newTotalQuantity + amount
                : newTotalQuantity - amount;
        } else {
            newTotalQuantity = manualEntry.type === 'Giriş'
                ? newTotalQuantity + amount
                : newTotalQuantity - amount;
        }

        batch.update(productRef, {
            quantity: newTotalQuantity,
            warehouseQuantities: newWarehouseQuantities
        });

        const movementData = {
            date: serverTimestamp(),
            type: `Manuel ${manualEntry.type} (${manualEntry.description})`,
            quantity: `${manualEntry.type === 'Giriş' ? '+' : '-'}${amount} ${product.unit || 'Adet'}`
        };

        if (manualEntry.warehouseId) {
            const warehouse = warehouses.find(w => w.id === manualEntry.warehouseId);
            movementData.warehouseId = manualEntry.warehouseId;
            movementData.warehouseName = warehouse?.name || 'Depo';
        }

        batch.set(movementRef, movementData);

        try {
            await batch.commit();
            setManualEntry({ quantity: '', type: 'Giriş', description: '', warehouseId: '' });
        } catch (error) {
            console.error("Manuel stok hareketi kaydedilirken hata:", error);
            toast.error("İşlem sırasında bir hata oluştu.");
        }
    };

    const handleCopyBarcode = () => {
        if (product?.barcode) {
            navigator.clipboard.writeText(product.barcode);
            setCopiedBarcode(true);
            setTimeout(() => setCopiedBarcode(false), 2000);
        }
    };

    const [showBarcodeModal, setShowBarcodeModal] = useState(false);

    const handlePrintBarcode = () => {
        if (!product?.barcode) {
            toast.error('Bu ürün için barkod tanımlanmamış.');
            return;
        }
        setShowBarcodeModal(true);
    };

    const handleExportExcel = () => {
        // Product info
        const productInfo = [{
            'Alan': 'Ürün Adı',
            'Değer': product.name
        }, {
            'Alan': 'Barkod',
            'Değer': product.barcode || '-'
        }, {
            'Alan': 'Birim',
            'Değer': product.unit || 'Adet'
        }, {
            'Alan': 'Mevcut Stok',
            'Değer': product.quantity
        }, {
            'Alan': 'Satış Fiyatı',
            'Değer': canViewPrices ? `${(product.salePrice || 0).toFixed(2)} ₺` : '***'
        }, {
            'Alan': 'Stok Değeri',
            'Değer': canViewPrices ? `${(product.quantity * (product.salePrice || 0)).toFixed(2)} ₺` : '***'
        }];

        // Movements data
        const movementsData = filteredMovements.map(move => ({
            'Tarih': move.date ? new Date(move.date.seconds * 1000).toLocaleString('tr-TR') : '-',
            'İşlem': move.type || '-',
            'Miktar': move.quantity || '-',
            'Depo': move.warehouseName || '-'
        }));

        const wb = XLSX.utils.book_new();
        const wsProduct = XLSX.utils.json_to_sheet(productInfo);
        const wsMovements = XLSX.utils.json_to_sheet(movementsData);

        XLSX.utils.book_append_sheet(wb, wsProduct, 'Ürün Bilgileri');
        XLSX.utils.book_append_sheet(wb, wsMovements, 'Stok Hareketleri');

        const fileName = `Stok_${product.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <div style={{ textAlign: 'center', color: '#64748b' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                <div>Yükleniyor...</div>
            </div>
        </div>
    );

    if (!product) return (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
            <h2 style={{ color: '#0f172a', marginBottom: '8px' }}>Ürün Bulunamadı</h2>
            <p>Aradığınız ürün mevcut değil veya silinmiş olabilir.</p>
            <Link to="/stok" style={{ ...styles.button, textDecoration: 'none', marginTop: '16px', display: 'inline-flex' }}>
                ← Stok Listesine Dön
            </Link>
        </div>
    );

    const availableQuantity = product.quantity - (product.blockedQuantity || 0);
    const stockValue = product.quantity * (product.salePrice || 0);

    const getStockStatus = () => {
        if (product.quantity <= 0) return { style: styles.statusCritical, text: 'Stok Yok', icon: '🚫' };
        if (product.quantity <= product.criticalLevel) return { style: styles.statusWarning, text: 'Kritik', icon: '⚠️' };
        return { style: styles.statusNormal, text: 'Normal', icon: '✓' };
    };

    const stockStatus = getStockStatus();

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={{
                ...styles.header,
                marginBottom: '32px'
            }}>
                <div style={styles.headerLeft}>
                    <Link to="/stok" style={styles.backLink}>
                        <span>←</span> Stok Listesine Dön
                    </Link>
                    <h1 style={{ ...styles.title }}>{product.name}</h1>
                    <div style={styles.subtitle}>
                        <span style={{ ...styles.statusBadge, ...stockStatus.style }}>
                            {stockStatus.icon} {stockStatus.text}
                        </span>
                    </div>
                    </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div style={styles.headerActions}>
                        <button onClick={handleDelete} style={{ ...styles.button, background: '#ef4444', padding: '10px 16px' }}>
                            🗑️
                        </button>
                        <button onClick={handleExportExcel} style={{ ...styles.buttonSecondary, padding: '10px 16px' }}>
                            📥
                        </button>
                        {isEditing ? (
                            <>
                                <button onClick={() => setIsEditing(false)} style={styles.buttonSecondary}>İptal</button>
                                <button onClick={handleSave} style={{ ...styles.button, ...styles.buttonSuccess }}>💾 Kaydet</button>
                            </>
                        ) : (
                            <button onClick={() => setIsEditing(true)} style={styles.button}>✏️ Düzenle</button>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={styles.summaryGrid}>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryIcon}>📦</div>
                    <div style={styles.summaryValue}>{product.quantity}</div>
                    <div style={styles.summaryLabel}>Mevcut Stok ({product.unit || 'Adet'})</div>
                </div>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryIcon}>💰</div>
                    <div style={styles.summaryValue}>
                        {canViewPrices ? `${stockValue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '***'}
                    </div>
                    <div style={styles.summaryLabel}>Stok Değeri</div>
                </div>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryIcon}>🔒</div>
                    <div style={styles.summaryValue}>{product.blockedQuantity || 0}</div>
                    <div style={styles.summaryLabel}>Blokaj ({product.unit || 'Adet'})</div>
                </div>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryIcon}>✅</div>
                    <div style={{ ...styles.summaryValue, color: '#06C167' }}>{availableQuantity}</div>
                    <div style={styles.summaryLabel}>Kullanılabilir ({product.unit || 'Adet'})</div>
                </div>
            </div>

            <div style={styles.grid}>
                {/* Left Column - Product Info */}
                <div>
                    <div style={styles.box}>
                        <h3 style={styles.sectionTitle}>📋 Ürün Bilgileri</h3>

                        {/* Product Image */}
                        {product.imageUrl ? (
                            <img
                                src={product.imageUrl}
                                alt={product.name}
                                style={styles.productImage}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <div style={styles.productImagePlaceholder}>📦</div>
                        )}

                        <div style={styles.formGroup}>
                            <ImageUpload
                                onImageUpload={handleImageUpload}
                                currentImageUrl={product.imageUrl}
                                label="Ürün Görseli"
                            />
                        </div>

                        {/* Barcode Display */}
                        {product.barcode && (
                            <div style={styles.barcodeContainer}>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>BARKOD</div>
                                    <div style={styles.barcodeText}>{product.barcode}</div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handleCopyBarcode} style={styles.copyButton}>
                                        {copiedBarcode ? '✓ Kopyalandı' : '📋 Kopyala'}
                                    </button>
                                    <button onClick={handlePrintBarcode} style={{ ...styles.copyButton, background: '#dbeafe', color: '#1d4ed8' }}>
                                        🖨️ Yazdır
                                    </button>
                                </div>
                            </div>
                        )}

                        <ul style={styles.infoList}>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Ürün Adı</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? <input style={styles.input} name="name" value={editedProduct.name} onChange={handleEditChange} /> : product.name}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Barkod</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? <input style={styles.input} name="barcode" value={editedProduct.barcode} onChange={handleEditChange} /> : product.barcode || '-'}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Satış Fiyatı</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? <input style={styles.input} name="salePrice" type="number" value={editedProduct.salePrice} onChange={handleEditChange} /> : canViewPrices ? `${(product.salePrice || 0).toFixed(2)} ₺` : '***'}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Ürün Grubu</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? (
                                        <select style={styles.select} name="groupId" value={editedProduct.groupId} onChange={handleEditChange}>
                                            <option value="">Grup Seçin</option>
                                            {productGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </select>
                                    ) : (productGroups.find(g => g.id === product.groupId)?.name || '-')}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Birim</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? <input style={styles.input} name="unit" value={editedProduct.unit} onChange={handleEditChange} /> : product.unit || 'Adet'}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Kritik Seviye</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? <input style={styles.input} name="criticalLevel" type="number" value={editedProduct.criticalLevel} onChange={handleEditChange} /> : `${product.criticalLevel} ${product.unit || 'Adet'}`}
                                </span>
                            </li>
                            {product.variantProperties && (
                                <li style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Varyant Özellikleri</span>
                                    <span style={styles.infoValue}>
                                        {Object.entries(product.variantProperties).map(([key, value]) => (
                                            <span key={key} style={{ marginLeft: '10px', background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                                                {key}: {value}
                                            </span>
                                        ))}
                                    </span>
                                </li>
                            )}
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Hammadde</span>
                                <span style={styles.infoValue}>
                                    {isEditing ? (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                            <input
                                                type="checkbox"
                                                name="isRawMaterial"
                                                checked={editedProduct.isRawMaterial || false}
                                                onChange={handleEditChange}
                                            />
                                            Hammadde olarak kullan
                                        </label>
                                    ) : (
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            background: product.isRawMaterial ? '#dbeafe' : '#f1f5f9',
                                            color: product.isRawMaterial ? '#1e40af' : '#64748b'
                                        }}>
                                            {product.isRawMaterial ? '✓ Hammadde' : 'Mamul'}
                                        </span>
                                    )}
                                </span>
                            </li>
                        </ul>

                        {/* Warehouse-based stock details */}
                        {product.warehouseQuantities && Object.keys(product.warehouseQuantities).length > 0 && (
                            <div style={{ marginTop: '24px' }}>
                                <h4 style={{ ...styles.sectionTitle, fontSize: '15px', marginBottom: '12px' }}>🏭 Depo Bazlı Stok</h4>
                                <ul style={styles.infoList}>
                                    {Object.entries(product.warehouseQuantities).map(([warehouseId, quantity]) => {
                                        const warehouse = warehouses.find(w => w.id === warehouseId);
                                        return warehouse && quantity > 0 ? (
                                            <li key={warehouseId} style={styles.infoItem}>
                                                <span style={styles.infoLabel}>{warehouse.name}</span>
                                                <span style={styles.infoValue}>{quantity} {product.unit || 'Adet'}</span>
                                            </li>
                                        ) : null;
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* Related Variants Section */}
                        {relatedVariants.length > 0 && (
                            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #f1f5f9' }}>
                                <h4 style={{ ...styles.sectionTitle, fontSize: '15px', marginBottom: '16px' }}>🔄 Diğer Varyantlar</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {relatedVariants.map(variant => (
                                        <Link
                                            key={variant.id}
                                            to={`/stok/${variant.id}`}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '10px',
                                                background: '#f8fafc',
                                                borderRadius: '8px',
                                                textDecoration: 'none',
                                                border: '1px solid #e2e8f0',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.borderColor = '#06C167'}
                                            onMouseOut={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                                        >
                                            {variant.imageUrl ? (
                                                <img src={variant.imageUrl} alt={variant.name} style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '40px', height: '40px', borderRadius: '4px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📦</div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{variant.name}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    {Object.entries(variant.variantProperties || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '13px', fontWeight: '600', color: variant.quantity <= (variant.criticalLevel || 0) ? '#ef4444' : '#10b981' }}>
                                                {variant.quantity} {variant.unit || 'Adet'}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Movements */}
                <div>
                    {/* Manual Entry Form */}
                    <div style={styles.box}>
                        <h3 style={styles.sectionTitle}>➕ Manuel Stok Hareketi</h3>
                        <form onSubmit={handleManualSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>İşlem Tipi</label>
                                    <select style={styles.select} name="type" value={manualEntry.type} onChange={handleManualChange}>
                                        <option>Giriş</option>
                                        <option>Çıkış</option>
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Miktar</label>
                                    <input style={styles.input} type="number" name="quantity" value={manualEntry.quantity} onChange={handleManualChange} placeholder="0" />
                                </div>
                            </div>
                            {warehouses.filter(w => w.isActive).length > 0 && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Depo</label>
                                    <select style={styles.select} name="warehouseId" value={manualEntry.warehouseId} onChange={handleManualChange}>
                                        <option value="">Tüm Depolar (Toplam)</option>
                                        {warehouses.filter(w => w.isActive).map(warehouse => (
                                            <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Açıklama</label>
                                <input style={styles.input} type="text" name="description" value={manualEntry.description} onChange={handleManualChange} placeholder="Örn: Sayım fazlası, Fire, İade" />
                            </div>
                            <button type="submit" style={{ ...styles.button, width: '100%' }}>
                                ✓ Hareketi Kaydet
                            </button>
                        </form>
                    </div>

                    {/* Stock Movements */}
                    <div style={styles.box}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>📊 Stok Hareketleri</h3>
                            <span style={{ fontSize: '13px', color: '#64748b' }}>
                                {filteredMovements.length} / {movements.length} hareket
                            </span>
                        </div>

                        {/* Filters */}
                        <div style={styles.filterContainer}>
                            <input
                                type="text"
                                placeholder="🔍 Ara..."
                                style={styles.filterInput}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <select
                                style={styles.filterSelect}
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">Tüm İşlemler</option>
                                <option value="in">Girişler</option>
                                <option value="out">Çıkışlar</option>
                            </select>
                            <input
                                type="date"
                                style={styles.filterInput}
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                                title="Başlangıç Tarihi"
                            />
                            <input
                                type="date"
                                style={styles.filterInput}
                                value={filterDateTo}
                                onChange={(e) => setFilterDateTo(e.target.value)}
                                title="Bitiş Tarihi"
                            />
                            {(searchTerm || filterType !== 'all' || filterDateFrom || filterDateTo) && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilterType('all');
                                        setFilterDateFrom('');
                                        setFilterDateTo('');
                                    }}
                                    style={{ ...styles.copyButton, background: '#fee2e2', color: '#dc2626' }}
                                >
                                    ✕ Temizle
                                </button>
                            )}
                        </div>

                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>İşlem</th>
                                        <th style={styles.th}>Miktar</th>
                                        <th style={styles.th}>Depo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredMovements.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                                Stok hareketi bulunamadı
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredMovements.map(move => (
                                            <tr key={move.id}>
                                                <td style={styles.td}>
                                                    {move.date ? new Date(move.date.seconds * 1000).toLocaleString('tr-TR') : '-'}
                                                </td>
                                                <td style={styles.td}>{move.type}</td>
                                                <td style={{
                                                    ...styles.td,
                                                    ...(move.quantity?.includes('+') ? styles.movementIn : styles.movementOut)
                                                }}>
                                                    {move.quantity}
                                                </td>
                                                <td style={styles.td}>{move.warehouseName || '-'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            {showBarcodeModal && (
                <BarcodePrintModal
                    product={product}
                    onClose={() => setShowBarcodeModal(false)}
                    canViewPrices={canViewPrices}
                />
            )}
        </div>
    );
}

export default StockDetail;
